import styled from 'styled-components';
import { colors, typography, spacing, borderRadius, shadows, breakpoints, zIndex } from '../../styles/theme';
import { fadeInUp } from '../../styles/animations';

export const Container = styled.div`
  width: 100%;
`;

// Pill selector — wraps on desktop, full-width stacked on mobile.
// Sticky so users can switch modes without scrolling back up.
export const ModeSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${spacing.sm};
  position: sticky;
  top: 90px; /* Below the app header (~90px) */
  z-index: ${zIndex.sticky};
  padding: ${spacing.md} 0;
  background: linear-gradient(
    to bottom,
    ${colors.neutral.white} 0%,
    ${colors.neutral.white} 70%,
    rgba(255, 255, 255, 0) 100%
  );

  @media (max-width: ${breakpoints.md}) {
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    width: 100%;
    top: 75px; /* Mobile header min-height */
    padding: ${spacing.md};
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const ModeTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: 0.625rem 1.25rem;
  border-radius: ${borderRadius.full};
  border: 1.5px solid ${colors.neutral.gray200};
  background: ${colors.neutral.white};
  color: ${colors.primary.teal};
  font-family: inherit;
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.bold};
  cursor: pointer;
  transition: all 0.25s ease;
  white-space: nowrap;
  flex-shrink: 0;

  ${({ $active }) =>
    $active
      ? `
        background: ${colors.gradients.orange};
        border-color: transparent;
        color: ${colors.neutral.white};
        box-shadow: ${shadows.orange};
        transform: scale(1.02);
      `
      : `
        &:hover {
          border-color: ${colors.primary.orange};
          transform: translateY(-2px);
        }
      `}

  &:focus-visible {
    outline: 2px solid ${colors.primary.orange};
    outline-offset: 2px;
  }

  @media (max-width: ${breakpoints.md}) {
    width: auto;
    justify-content: center;
  }
`;

export const ModeTabIcon = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;
`;

// Active mode card. Minimal style: no background, no gradient border.
// Keeps only subtle card shadow and entrance animation.
// Content is centered with max-width for readability.
export const ModeCard = styled.div`
  position: relative;
  margin-top: ${spacing['2xl']};
  padding: ${spacing.xl} ${spacing['2xl']};
  background: transparent;
  border-radius: ${borderRadius['3xl']};
  box-shadow: ${shadows.card};
  // backwards (not both) fill: entrance anim only fills during the delay so the
  // completed animation does not pin transform and kill the hover lift.
  animation: ${fadeInUp} 0.4s ease-out backwards;
  transition: all 0.3s ease;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing.lg};
    margin-top: ${spacing.xl};
  }
`;

export const ModeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
  flex-wrap: wrap;

  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
    text-align: center;
  }
`;

export const ModeIcon = styled.div`
  width: 56px;
  height: 56px;
  min-width: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.full};
  background: ${colors.gradients.orange};
  color: ${colors.neutral.white};
  font-size: 1.5rem;
  box-shadow: ${shadows.orange};
`;

export const ModeTitle = styled.h3`
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.primary.teal};
  margin: 0;
`;

export const ModeTagline = styled.p`
  font-size: ${typography.fontSize.base};
  color: ${colors.neutral.gray600};
  line-height: ${typography.lineHeight.relaxed};
  margin: ${spacing.xs} 0 0 0;
`;

export const Example = styled.blockquote`
  margin: ${spacing.lg} 0;
  padding: ${spacing.md} ${spacing.lg};
  background: ${colors.neutral.gray100};
  border-left: 4px solid ${colors.primary.orange};
  border-radius: ${borderRadius.lg};
  font-style: italic;
  font-size: ${typography.fontSize.lg};
  color: ${colors.primary.teal};
`;

export const Outcomes = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  margin: ${spacing.lg} 0;
`;

export const OutcomeChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: ${borderRadius.xl};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.medium};
  line-height: ${typography.lineHeight.relaxed};

  svg {
    flex-shrink: 0;
    font-size: 0.9rem;
  }

  ${({ $status }) => {
    if ($status === 'win') {
      return `background: rgba(30, 76, 92, 0.06); color: ${colors.primary.teal};`;
    }
    if ($status === 'continue') {
      return `background: rgba(217, 125, 43, 0.08); color: ${colors.primary.orange};`;
    }
    return `background: rgba(225, 86, 45, 0.08); color: ${colors.secondary.red};`;
  }}
`;

export const ChipsBlock = styled.div`
  margin: ${spacing.lg} 0;
`;

export const ChipsTitle = styled.h4`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${spacing.sm} 0;
  color: ${({ $forbidden }) => ($forbidden ? colors.secondary.red : colors.primary.teal)};
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};

  svg {
    font-size: 0.8rem;
  }

  ${({ $forbidden }) =>
    $forbidden
      ? `
        background: rgba(225, 86, 45, 0.08);
        color: ${colors.secondary.red};
        border: 1px solid rgba(225, 86, 45, 0.25);
      `
      : `
        background: rgba(30, 76, 92, 0.06);
        color: ${colors.primary.teal};
        border: 1px solid rgba(30, 76, 92, 0.15);
      `}
`;

export const Nested = styled.div`
  margin-top: ${spacing.xl};
`;

export const Closing = styled.p`
  margin: ${spacing.lg} 0 0 0;
  font-size: ${typography.fontSize.base};
  color: ${colors.neutral.gray600};
  line-height: ${typography.lineHeight.relaxed};
  font-style: italic;
  text-align: center;
`;
